import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Backdrop,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Edit,
  Delete,
  MonetizationOn,
  DirectionsCar,
  Construction,
  Refresh,
  Close,
  CameraAlt,
  DeleteOutline,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  equipmentAPI,
  vehicleAPI,
  vehiclePriceAPI,
  VehiclePriceHistoryRow,
  equipmentPriceAPI,
  EquipmentPriceHistoryRow,
} from '../../services/api';
import { Equipment, EquipmentFormData, EquipmentCreateData, EquipmentFilters } from '../../types/equipment';

const EquipmentPage: React.FC = () => {
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);
  // 當前選中的標籤
  const [currentTab, setCurrentTab] = useState(0);
  
  // 車輛相關狀態
  const [vehicles, setVehicles] = useState<Equipment[]>([]);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  
  // 機具相關狀態
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  
  // 通用狀態
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Equipment | null>(null);
  const [dialogType, setDialogType] = useState<'vehicle' | 'equipment'>('vehicle');
  const [filters, setFilters] = useState<EquipmentFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState<EquipmentFormData>({
    equipment_code: '', // 編號由後端自動生成，前端不處理
    name: '',
    license_plate: '',
    mileage: 0,
    status: 'available',
    info: '',
  });
  const [photos, setPhotos] = useState<(File | undefined)[]>(new Array(4).fill(undefined));
  const [photoUrls, setPhotoUrls] = useState<string[]>(new Array(4).fill('')); // 用於顯示（Blob URL）
  const [photoOriginalPaths, setPhotoOriginalPaths] = useState<string[]>(new Array(4).fill('')); // 用於保存原始路徑
  const [photoLoading, setPhotoLoading] = useState<boolean[]>(new Array(4).fill(false));
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [photoViewer, setPhotoViewer] = useState<{ open: boolean; src: string }>({ open: false, src: '' });

  type VehiclePriceForm = {
    hourly_rate: number;
    half_day_threshold_hours: number;
    extra_mileage_threshold_km: number;
    extra_mileage_rate_per_km: number;
    half_day_without_driver_rate: number;
    half_day_with_driver_rate: number;
    full_day_without_driver_rate: number;
    full_day_with_driver_rate: number;
  };

  // 價格管理 Dialog
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceKind, setPriceKind] = useState<'vehicle' | 'equipment'>('vehicle');
  const [priceVehicle, setPriceVehicle] = useState<Equipment | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceHistory, setPriceHistory] = useState<VehiclePriceHistoryRow[]>([]);
  const [equipmentPriceHistory, setEquipmentPriceHistory] = useState<EquipmentPriceHistoryRow[]>([]);
  const [equipmentPriceForm, setEquipmentPriceForm] = useState({ day_rate: 0, hourly_rate: 0 });
  const [expandedHistoryKey, setExpandedHistoryKey] = useState<string | false>(false);
  const [priceForm, setPriceForm] = useState<VehiclePriceForm>({
    hourly_rate: 0,
    half_day_threshold_hours: 0,
    extra_mileage_threshold_km: 0,
    extra_mileage_rate_per_km: 0,
    half_day_without_driver_rate: 0,
    half_day_with_driver_rate: 0,
    full_day_without_driver_rate: 0,
    full_day_with_driver_rate: 0,
  });

  useEffect(() => {
    fetchVehicles();
    fetchEquipment();
  }, [filters]);

  useEffect(() => {
    sessionExpiredRef.current = false;
  }, []);

  const fetchVehicles = async () => {
    try {
      setVehicleLoading(true);
      const response = await vehicleAPI.getVehicles({ ...filters });
      console.log('車輛數據:', response);
      
      // API 已經處理了分頁格式
      setVehicles(response);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleSessionExpired();
        return;
      }
      console.error('載入車輛資料失敗:', error);
      showSnackbar('載入車輛資料失敗', 'error');
      setVehicles([]);
    } finally {
      setVehicleLoading(false);
    }
  };

  const fetchEquipment = async () => {
    try {
      setEquipmentLoading(true);
      const response = await equipmentAPI.getEquipment({ ...filters, type: 'equipment' });
      console.log('機具數據:', response);
      
      // API 已經處理了分頁格式
      setEquipment(response);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleSessionExpired();
        return;
      }
      console.error('載入機具資料失敗:', error);
      showSnackbar('載入機具資料失敗', 'error');
      setEquipment([]);
    } finally {
      setEquipmentLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 照片處理函數
  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0]; // 每次只選擇一張照片

    // 檢查文件類型
    if (!file.type.startsWith('image/')) {
      showSnackbar('請選擇圖片文件', 'error');
      return;
    }

    // 檢查文件大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('圖片大小不能超過 5MB', 'error');
      return;
    }

    // 更新指定索引的照片
    setPhotos(prev => {
      const newPhotos = [...prev];
      // 如果之前有照片，先釋放 URL
      if (newPhotos[index] && photoUrls[index]) {
        URL.revokeObjectURL(photoUrls[index]);
      }
      newPhotos[index] = file;
      return newPhotos;
    });

    setPhotoUrls(prev => {
      const newUrls = [...prev];
      // 如果之前有 URL，先釋放
      if (newUrls[index]) {
        URL.revokeObjectURL(newUrls[index]);
      }
      newUrls[index] = URL.createObjectURL(file);
      return newUrls;
    });

    // 重置 input 值，以便可以再次選擇相同文件
    event.target.value = '';
  };

  const handlePhotoRemove = (index: number) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      newPhotos[index] = undefined as any;
      return newPhotos;
    });
    setPhotoUrls(prev => {
      if (prev[index]) {
        URL.revokeObjectURL(prev[index]);
      }
      const newUrls = [...prev];
      newUrls[index] = '';
      return newUrls;
    });
  };

  const handlePhotoCardClick = (index: number) => {
    // 如果已有照片（本地上傳或從服務器載入），點擊時放大查看
    if (photos[index] || photoUrls[index]) {
      const photoSrc = photos[index] 
        ? URL.createObjectURL(photos[index]!) 
        : photoUrls[index];
      setPhotoViewer({ open: true, src: photoSrc });
    } else {
      // 如果沒有照片，觸發對應索引的文件選擇
      const input = document.getElementById(`photo-upload-${index}`) as HTMLInputElement;
      if (input) {
        input.click();
      }
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm || undefined,
    }));
  };

  const handleFilterChange = (field: keyof EquipmentFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      equipment_code: '',
      name: '',
      license_plate: '',
      mileage: 0,
      status: 'available',
      info: '',
    });
    setPhotos(new Array(4).fill(undefined));
    setPhotoUrls(new Array(4).fill(''));

    setPhotoOriginalPaths(new Array(4).fill(''));
    setPhotoLoading(new Array(4).fill(false));
  };

  const handleSessionExpired = () => {
    if (sessionExpiredRef.current) {
      return;
    }
    sessionExpiredRef.current = true;
    showSnackbar('登入已過期，請重新登入', 'error');
    localStorage.removeItem('token');
    handleCloseDialog();
    navigate('/login', { replace: true });
  };

  const handleAddItem = (type: 'vehicle' | 'equipment') => {
    setEditingItem(null);
    setDialogType(type);
    setFormData({
      equipment_code: '', // 編號由後端自動生成
      name: '',
      license_plate: '',
      mileage: 0,
      status: 'available',
      info: '',
    });
    // 初始化為固定長度 4 的數組
    setPhotos(new Array(4).fill(undefined));
    setPhotoUrls(new Array(4).fill(''));
    setPhotoOriginalPaths(new Array(4).fill(''));
    setPhotoLoading(new Array(4).fill(false));
    setOpenDialog(true);
  };

  const handleEditItem = async (item: Equipment, type: 'vehicle' | 'equipment') => {
    try {
      setEditingItem(item);
      setDialogType(type);
      setFormData({
        equipment_code: item.equipment_code || '',
        name: item.name,
        license_plate: item.license_plate || '',
        mileage: item.mileage || 0,
        status: item.status,
        info: item.info || '',
      });
      
      // 先打開對話框，照片載入失敗也不影響
      setOpenDialog(true);
      
      // 從資料庫讀取照片路徑並顯示（異步載入，不阻塞對話框顯示）
      const photoPaths = [
        item.photo_path_1 || '',
        item.photo_path_2 || '',
        item.photo_path_3 || '',
        item.photo_path_4 || ''
      ];
      let sessionExpiredTriggered = false;
      const triggerSessionExpired = () => {
        if (!sessionExpiredTriggered) {
          sessionExpiredTriggered = true;
          handleSessionExpired();
        }
      };
      
      // 儲存原始路徑（用於發送給後端）
      setPhotoOriginalPaths(photoPaths);
      
      // 如果有照片路徑，才嘗試載入
      const hasPhotos = photoPaths.some(path => path);
      if (!hasPhotos) {
        // 沒有照片，直接清空
        setPhotoUrls(new Array(4).fill(''));
        setPhotoOriginalPaths(new Array(4).fill(''));
        setPhotos(new Array(4).fill(undefined));
        setPhotoLoading(new Array(4).fill(false));
        return;
      }
      
      // 構建照片 URL（如果有照片路徑）
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';
      const token = localStorage.getItem('token');
      
      // 初始化 loading 狀態（有照片路徑的設為 true）
      const initialLoading = photoPaths.map(path => path !== '');
      setPhotoLoading(initialLoading);
      
      // 使用 Promise.all 來並行載入所有照片（不阻塞對話框）
      const photoUrlPromises = photoPaths.map(async (path, index) => {
        if (path) {
          try {
            // 使用 fetch 獲取照片（帶上 Authorization header）
            const response = await fetch(`${baseUrl}/vehicle/photo/${encodeURIComponent(path)}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.status === 401 || response.status === 403) {
              triggerSessionExpired();
              return '';
            }
            
            if (response.ok) {
              // 轉換為 blob URL
              const blob = await response.blob();
              return URL.createObjectURL(blob);
            } else {
              console.warn(`照片 ${index + 1} 載入失敗:`, response.status);
              return '';
            }
          } catch (error) {
            console.warn(`照片 ${index + 1} 載入錯誤:`, error);
            // 若因為認證錯誤導致的網路異常，也觸發重新登入
            const message = (error as Error)?.message || '';
            if (message.toLowerCase().includes('403') || message.toLowerCase().includes('401')) {
              triggerSessionExpired();
            }
            return '';
          }
        }
        return '';
      });
      
      // 等待所有照片載入完成（不阻塞對話框顯示）
      Promise.all(photoUrlPromises).then((newPhotoUrls) => {
        if (sessionExpiredTriggered) {
          return;
        }
        // 重置照片狀態（不清除已上傳的新照片）
        setPhotoUrls(newPhotoUrls);
        // 清空照片文件（因為是從資料庫讀取的，不是新上傳的）
        setPhotos(new Array(4).fill(undefined));
        // 關閉所有 loading
        setPhotoLoading(new Array(4).fill(false));
      }).catch((error) => {
        console.error('照片載入失敗:', error);
        if (sessionExpiredTriggered) {
          return;
        }
        // 即使載入失敗，也清空狀態
        setPhotoUrls(new Array(4).fill(''));
        setPhotoOriginalPaths(new Array(4).fill(''));
        setPhotos(new Array(4).fill(undefined));
        setPhotoLoading(new Array(4).fill(false));
      });
      
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleSessionExpired();
        return;
      }
      console.error('打開編輯對話框失敗:', error);
      // 即使出錯，也嘗試打開對話框
      setOpenDialog(true);
      // 清空照片狀態
      setPhotoUrls(new Array(4).fill(''));
      setPhotoOriginalPaths(new Array(4).fill(''));
      setPhotos(new Array(4).fill(undefined));
      setPhotoLoading(new Array(4).fill(false));
    }
  };

  const handleSaveItem = async () => {
    try {
      console.log('儲存數據:', formData);
      
      if (editingItem) {
        // 更新模式：確認照片修改
        const currentPhotoCount = photos.filter(p => p !== undefined).length + photoUrls.filter(url => url !== '').length;
        const originalPhotoCount = [
          editingItem.photo_path_1,
          editingItem.photo_path_2,
          editingItem.photo_path_3,
          editingItem.photo_path_4
        ].filter(path => path).length;
        
        // 計算新照片和保留的照片
        const newPhotos = photos.filter(p => p !== undefined).length;
        const keptPhotos = photoUrls.filter(url => url !== '').length;
        const deletedPhotos = originalPhotoCount - keptPhotos;
        
        let confirmMessage = `確定要更新此${dialogType === 'vehicle' ? '車輛' : '機具'}嗎？\n\n`;
        confirmMessage += `照片變更：\n`;
        confirmMessage += `- 原有照片：${originalPhotoCount} 張\n`;
        if (newPhotos > 0) {
          confirmMessage += `- 新上傳：${newPhotos} 張\n`;
        }
        if (keptPhotos > 0) {
          confirmMessage += `- 保留：${keptPhotos} 張\n`;
        }
        if (deletedPhotos > 0) {
          confirmMessage += `- 刪除：${deletedPhotos} 張\n`;
        }
        confirmMessage += `\n最終將有 ${currentPhotoCount} 張照片`;
        
        if (!window.confirm(confirmMessage)) {
          return; // 使用者取消
        }
        
        console.log('更新項目:', editingItem.id);
        
        // 準備照片數據
        // 1. 收集新上傳的照片及其位置
        const photoFilesWithPositions: { file: File; position: number }[] = [];
        photos.forEach((photo, index) => {
          if (photo) {
            photoFilesWithPositions.push({ file: photo, position: index });
          }
        });
        
        // 2. 保留的舊照片路徑（4 個位置）
        // 使用原始路徑（從資料庫來的），而不是 Blob URL
        const keepPhotoUrls = photoOriginalPaths.map((path, index) => {
          // 如果有新上傳的照片，這個位置就不保留舊照片
          if (photos[index]) {
            return null;
          }
          // 返回原始路徑
          return path || null;
        });
        
        // 3. 記錄每個位置的狀態（用於後端了解完整狀態）
        const photoPositions = photos.map((photo, index) => {
          if (photo) {
            return index; // 有新照片
          } else if (photoUrls[index]) {
            return -1; // 保留舊照片
          } else {
            return null; // 空位置
          }
        });
        
        console.log('照片狀態:', {
          newPhotos: photoFilesWithPositions.map(p => `位置${p.position + 1}`),
          keepPhotos: keepPhotoUrls.map((url, i) => url ? `位置${i + 1}` : null).filter(Boolean),
          positions: photoPositions
        });
        
        // 準備更新數據
        const updateDataWithPhotos = {
          ...formData,
          license_plate: dialogType === 'vehicle' ? formData.license_plate : '',
          mileage: dialogType === 'vehicle' ? formData.mileage : 0,
          photos: photoFilesWithPositions.map(p => p.file), // 新照片文件
          photo_positions: photoFilesWithPositions.map(p => p.position), // 新照片的位置
          keep_photo_urls: keepPhotoUrls // 保留的照片 URL
        };
        
        // 設定上傳進度
        if (photoFilesWithPositions.length > 0) {
          setUploadProgress({ current: 0, total: photoFilesWithPositions.length });
          console.log(`開始上傳 ${photoFilesWithPositions.length} 張照片...`);
        }
      
      // 鎖住整個頁面
      setIsLoading(true);
      setUploadProgress({ current: 0, total: 0 });

      if (dialogType === 'vehicle') {
        await vehicleAPI.updateVehicle(editingItem.id, updateDataWithPhotos);
      } else {
        await equipmentAPI.updateEquipment(editingItem.id, updateDataWithPhotos);
      }
        // 顯示完成提示
        alert(`${dialogType === 'vehicle' ? '車輛' : '機具'}更新成功！\n\n照片更新：\n- 新上傳：${newPhotos} 張\n- 保留：${keptPhotos} 張\n- 刪除：${deletedPhotos} 張`);
        showSnackbar(`${dialogType === 'vehicle' ? '車輛' : '機具'}更新成功`, 'success');
      } else {
        // 新增模式
        console.log('新增項目');
        
        // 鎖住整個頁面
        setIsLoading(true);
        setUploadProgress({ current: 0, total: 0 });
        
        // 新增時不發送 equipment_code，讓後端自動生成
        const createData: EquipmentCreateData = {
          name: formData.name,
          license_plate: dialogType === 'vehicle' ? formData.license_plate : '',
          mileage: dialogType === 'vehicle' ? formData.mileage : 0,
          status: formData.status,
          info: formData.info,
        };
        // 準備照片數據（過濾掉 undefined）
        const photoFiles = photos.filter((photo): photo is File => photo !== undefined);
        
        // 創建包含照片的數據
        const createDataWithPhotos = {
          ...createData,
          photos: photoFiles
        };
        
        // 設定上傳進度
        if (photoFiles.length > 0) {
          setUploadProgress({ current: 0, total: photoFiles.length });
          console.log(`開始上傳 ${photoFiles.length} 張照片...`);
        }

      const newEquipment = dialogType === 'vehicle'
        ? await vehicleAPI.createVehicle(createDataWithPhotos)
        : await equipmentAPI.createEquipment(createDataWithPhotos);
        
        // 顯示成功訊息
        if (dialogType === 'vehicle') {
          alert(`車輛新增成功！\n\n車輛資訊：\n- 名稱：${newEquipment.name}\n- 車牌：${newEquipment.license_plate || '-'}\n- 狀態：${newEquipment.status}\n- 照片：${photoFiles.length} 張`);
        } else {
          alert(`機具新增成功！\n\n機具資訊：\n- 名稱：${newEquipment.name}\n- 狀態：${newEquipment.status}\n- 照片：${photoFiles.length} 張`);
        }
        
        showSnackbar(`${dialogType === 'vehicle' ? '車輛' : '機具'}新增成功`, 'success');
      }
      
      handleCloseDialog();
      fetchVehicles();
      fetchEquipment();
      
  } catch (error: any) {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      handleSessionExpired();
      return;
    }
    console.error('儲存失敗:', error);
    console.error('錯誤詳情:', error.response?.data);
    showSnackbar(`儲存失敗: ${error.response?.data?.detail || error.message}`, 'error');
  } finally {
      // 解鎖頁面
      setIsLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleDeleteItem = async (id: number, type: 'vehicle' | 'equipment') => {
    if (window.confirm('確定要刪除此項目嗎？')) {
      try {
        if (type === 'vehicle') {
          await vehicleAPI.deleteVehicle(id);
        } else {
          await equipmentAPI.deleteEquipment(id);
        }
        showSnackbar(`${type === 'vehicle' ? '車輛' : '機具'}刪除成功`, 'success');
        fetchVehicles();
        fetchEquipment();
      } catch (error) {
        if ((error as any)?.response?.status === 401 || (error as any)?.response?.status === 403) {
          handleSessionExpired();
          return;
        }
        showSnackbar('刪除失敗', 'error');
      }
    }
  };

  const handlePriceManage = (item: Equipment, assetType: 'vehicle' | 'equipment') => {
    (async () => {
      try {
        setPriceKind(assetType);
        setPriceVehicle(item);
        setPriceDialogOpen(true);
        setPriceLoading(true);
        setExpandedHistoryKey(false);
        if (assetType === 'vehicle') {
          const [row, history] = await Promise.all([
            vehiclePriceAPI.get(item.id),
            vehiclePriceAPI.history(item.id),
          ]);
          setPriceHistory(Array.isArray(history) ? history : []);
          setEquipmentPriceHistory([]);
          setPriceForm({
            hourly_rate: row.hourly_rate == null ? 0 : Number(row.hourly_rate),
            half_day_threshold_hours: row.half_day_threshold_hours == null ? 0 : Number(row.half_day_threshold_hours),
            extra_mileage_threshold_km: row.extra_mileage_threshold_km == null ? 0 : Number(row.extra_mileage_threshold_km),
            extra_mileage_rate_per_km: row.extra_mileage_rate_per_km == null ? 0 : Number(row.extra_mileage_rate_per_km),
            half_day_without_driver_rate: row.half_day_without_driver_rate == null ? 0 : Number(row.half_day_without_driver_rate),
            half_day_with_driver_rate: row.half_day_with_driver_rate == null ? 0 : Number(row.half_day_with_driver_rate),
            full_day_without_driver_rate: row.full_day_without_driver_rate == null ? 0 : Number(row.full_day_without_driver_rate),
            full_day_with_driver_rate: row.full_day_with_driver_rate == null ? 0 : Number(row.full_day_with_driver_rate),
          });
        } else {
          const [row, history] = await Promise.all([
            equipmentPriceAPI.get(item.id),
            equipmentPriceAPI.history(item.id),
          ]);
          setPriceHistory([]);
          setEquipmentPriceHistory(Array.isArray(history) ? history : []);
          setEquipmentPriceForm({
            day_rate: row.day_rate == null ? 0 : Number(row.day_rate),
            hourly_rate: row.hourly_rate == null ? 0 : Number(row.hourly_rate),
          });
        }
      } catch (e: any) {
        console.error('載入價格失敗:', e);
        showSnackbar('載入價格失敗', 'error');
      } finally {
        setPriceLoading(false);
      }
    })();
  };

  const handleClosePriceDialog = () => {
    setPriceDialogOpen(false);
    setPriceVehicle(null);
    setPriceKind('vehicle');
    setPriceLoading(false);
    setPriceSaving(false);
    setPriceHistory([]);
    setEquipmentPriceHistory([]);
    setEquipmentPriceForm({ day_rate: 0, hourly_rate: 0 });
    setExpandedHistoryKey(false);
  };

  const handleSavePriceDialog = async () => {
    if (!priceVehicle) return;
    try {
      setPriceSaving(true);
      const id = priceVehicle.id;
      if (priceKind === 'equipment') {
        await equipmentPriceAPI.put(id, {
          day_rate: equipmentPriceForm.day_rate,
          hourly_rate: equipmentPriceForm.hourly_rate,
          is_active: true,
        });
        const h = await equipmentPriceAPI.history(id);
        setEquipmentPriceHistory(Array.isArray(h) ? h : []);
      } else {
        await vehiclePriceAPI.put(id, {
          hourly_rate: priceForm.hourly_rate,
          half_day_threshold_hours: priceForm.half_day_threshold_hours,
          extra_mileage_threshold_km: priceForm.extra_mileage_threshold_km,
          extra_mileage_rate_per_km: priceForm.extra_mileage_rate_per_km,
          half_day_without_driver_rate: priceForm.half_day_without_driver_rate,
          half_day_with_driver_rate: priceForm.half_day_with_driver_rate,
          full_day_without_driver_rate: priceForm.full_day_without_driver_rate,
          full_day_with_driver_rate: priceForm.full_day_with_driver_rate,
          is_active: true,
        });
        const h = await vehiclePriceAPI.history(id);
        setPriceHistory(Array.isArray(h) ? h : []);
      }

      setPriceDialogOpen(false);
      showSnackbar('價格已更新', 'success');
    } catch (e: any) {
      console.error('儲存價格失敗:', e);
      showSnackbar(`儲存失敗：${e?.response?.data?.detail || e?.message || '未知錯誤'}`, 'error');
    } finally {
      setPriceSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'rented': return 'warning';
      case 'maintenance': return 'error';
      case 'retired': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可用';
      case 'rented': return '租借中';
      case 'maintenance': return '維修中';
      case 'retired': return '已退役';
      default: return status;
    }
  };

  const renderTable = (items: Equipment[], loading: boolean, type: 'vehicle' | 'equipment') => (
    <Card sx={{ 
      height: '100%',
      width: '100%',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid rgba(255,255,255,0.2)',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: type === 'vehicle' 
          ? 'linear-gradient(90deg, #2196f3 0%, #42a5f5 100%)'
          : 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)',
        zIndex: 1
      }
    }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 標題和操作按鈕 */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,249,250,0.9) 100%)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)'
          }
        }}>
          {/* 搜索和篩選 */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            p: 3,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <TextField
              size="small"
              placeholder={`搜索${type === 'vehicle' ? '車輛' : '機具'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'white',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  },
                  '&.Mui-focused': {
                    boxShadow: type === 'vehicle' 
                      ? '0 0 0 2px rgba(33,150,243,0.2)'
                      : '0 0 0 2px rgba(255,152,0,0.2)'
                  }
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<Search />}
              onClick={handleSearch}
              size="small"
              sx={{
                borderRadius: 2,
                borderColor: type === 'vehicle' ? '#2196f3' : '#ff9800',
                color: type === 'vehicle' ? '#1976d2' : '#f57c00',
                '&:hover': {
                  borderColor: type === 'vehicle' ? '#1976d2' : '#f57c00',
                  background: type === 'vehicle' 
                    ? 'rgba(33,150,243,0.04)'
                    : 'rgba(255,152,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              搜索
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ 
                color: type === 'vehicle' ? '#1976d2' : '#f57c00',
                '&.Mui-focused': {
                  color: type === 'vehicle' ? '#1976d2' : '#f57c00'
                }
              }}>
                狀態
              </InputLabel>
              <Select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="狀態"
                sx={{
                  borderRadius: 2,
                  background: 'white',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: type === 'vehicle' ? '#2196f3' : '#ff9800',
                    borderWidth: 2
                  }
                }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="available">可用</MenuItem>
                <MenuItem value="rented">租借中</MenuItem>
                <MenuItem value="maintenance">維修中</MenuItem>
                <MenuItem value="retired">已退役</MenuItem>
              </Select>
            </FormControl>
            <IconButton 
              onClick={() => {
                if (type === 'vehicle') fetchVehicles();
                else fetchEquipment();
              }}
              sx={{
                color: type === 'vehicle' ? '#1976d2' : '#f57c00',
                background: type === 'vehicle' 
                  ? 'rgba(33,150,243,0.1)'
                  : 'rgba(255,152,0,0.1)',
                '&:hover': {
                  background: type === 'vehicle' 
                    ? 'rgba(33,150,243,0.2)'
                    : 'rgba(255,152,0,0.2)',
                  transform: 'rotate(180deg)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Refresh />
            </IconButton>
            
            {/* 新增按鈕 */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleAddItem(type)}
              size="small"
              sx={{
                background: type === 'vehicle' 
                  ? 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)'
                  : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                boxShadow: type === 'vehicle' 
                  ? '0 4px 12px rgba(33,150,243,0.3)'
                  : '0 4px 12px rgba(255,152,0,0.3)',
                '&:hover': {
                  background: type === 'vehicle' 
                    ? 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)'
                    : 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: type === 'vehicle' 
                    ? '0 6px 16px rgba(33,150,243,0.4)'
                    : '0 6px 16px rgba(255,152,0,0.4)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              新增{type === 'vehicle' ? '車輛' : '機具'}
            </Button>
          </Box>
        </Box>

        {/* 表格內容 */}
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,249,250,0.8) 100%)'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress 
                sx={{ 
                  color: type === 'vehicle' ? '#2196f3' : '#ff9800',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round'
                  }
                }} 
              />
              <Typography variant="body2" color="text.secondary">
                載入{type === 'vehicle' ? '車輛' : '機具'}資料中...
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ 
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.2)',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: type === 'vehicle' 
                  ? 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)'
                  : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                borderRadius: '4px',
                '&:hover': {
                  background: type === 'vehicle' 
                    ? 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)'
                    : 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)'
                }
              }
            }}>
              <Table
                stickyHeader
                sx={{
                  '& .MuiTableCell-root': {
                    fontSize: '1.2rem',
                    padding: '16px 12px',
                    textAlign: 'center',
                    border: '1px solid rgba(0,0,0,0.12)'
                  },
                  '& .MuiTableCell-head': {
                    textAlign: 'center',
                    border: '1px solid rgba(0,0,0,0.2)'
                  }
                }}
              >
                       <TableHead>
                         <TableRow sx={{
                           background: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
                           '& .MuiTableCell-head': {
                             color: 'white',
                             fontWeight: 'bold',
                             fontSize: '1.2rem',
                             textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                             borderBottom: '1px solid rgba(255,255,255,0.2)',
                             borderRight: '1px solid rgba(255,255,255,0.15)',
                             background: 'transparent',
                             '&:last-child': {
                               borderRight: 'none'
                             }
                           }
                         }}>
                           <TableCell sx={{ textAlign: 'center' }}>編號</TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>名稱</TableCell>
                           {type === 'vehicle' && (
                             <TableCell sx={{ textAlign: 'center' }}>車牌</TableCell>
                           )}
                           {type === 'vehicle' && (
                             <TableCell sx={{ textAlign: 'center' }}>里程</TableCell>
                           )}
                           <TableCell sx={{ textAlign: 'center' }}>狀態</TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>備註</TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>操作</TableCell>
                         </TableRow>
                       </TableHead>
                <TableBody>
                  {Array.isArray(items) ? items.map((item, index) => (
                    <TableRow 
                      key={item.id} 
                      hover
                      sx={{
                        '&:nth-of-type(even)': {
                          background: 'rgba(0,0,0,0.02)'
                        },
                        '&:hover': {
                          background: type === 'vehicle' 
                            ? 'rgba(33,150,243,0.08)'
                            : 'rgba(255,152,0,0.08)',
                          transform: 'scale(1.01)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        },
                        transition: 'all 0.2s ease',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                          borderRight: '1px solid rgba(0,0,0,0.08)',
                          py: 1.5,
                          '&:last-child': {
                            borderRight: 'none'
                          }
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: type === 'vehicle' ? '#1976d2' : '#f57c00',
                        fontFamily: 'monospace'
                      }}>
                        {item.id}
                      </TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center',
                        fontWeight: 'medium',
                        color: 'text.primary'
                      }}>
                        {item.name}
                      </TableCell>
                      {type === 'vehicle' && (
                        <TableCell sx={{ 
                          textAlign: 'center',
                          color: 'text.secondary'
                        }}>
                          {item.license_plate || '-'}
                        </TableCell>
                      )}
                      {type === 'vehicle' && (
                        <TableCell sx={{ 
                          textAlign: 'center',
                          color: 'text.secondary'
                        }}>
                          {item.mileage ? `${item.mileage.toLocaleString()} km` : '-'}
                        </TableCell>
                      )}
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={getStatusText(item.status)}
                          color={getStatusColor(item.status) as any}
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            height: '32px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '& .MuiChip-label': {
                              px: 1.5,
                              fontSize: '1rem'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center',
                        color: 'text.secondary'
                      }}>
                        {item.info || '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="價格管理" arrow>
                            <IconButton
                              size="medium"
                              onClick={() => handlePriceManage(item, type)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: '#2e7d32',
                                  background: 'rgba(46,125,50,0.1)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <MonetizationOn fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="編輯" arrow>
                            <IconButton 
                              size="medium"
                              onClick={() => handleEditItem(item, type)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: type === 'vehicle' ? '#2196f3' : '#ff9800',
                                  background: type === 'vehicle' 
                                    ? 'rgba(33,150,243,0.1)'
                                    : 'rgba(255,152,0,0.1)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Edit fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除" arrow>
                            <IconButton 
                              size="medium"
                              onClick={() => handleDeleteItem(item.id, type)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: '#f44336',
                                  background: 'rgba(244,67,54,0.1)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Delete fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )) : null}
                  {Array.isArray(items) && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={type === 'vehicle' ? 7 : 6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          暫無{type === 'vehicle' ? '車輛' : '機具'}資料
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ pt: 0.5, px: 3, pb: 1, height: 'calc(100vh - 100px)' }}>
      {/* 標籤頁導航 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1976d2',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab 
            icon={<DirectionsCar />} 
            iconPosition="start"
            label="車輛管理" 
            sx={{ 
              mr: 2,
              px: 1.5,
              minHeight: 36,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              textTransform: 'none',
              '&.Mui-selected': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25,118,210,0.08)'
              }
            }}
          />
          <Tab 
            icon={<Construction />} 
            iconPosition="start"
            label="機具管理" 
            sx={{ 
              px: 1.5,
              minHeight: 36,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              textTransform: 'none',
              '&.Mui-selected': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25,118,210,0.08)'
              }
            }}
          />
        </Tabs>
      </Box>

      {/* 標籤頁內容 */}
      <Box sx={{ height: 'calc(100% - 60px)' }}>
        {currentTab === 0 && renderTable(vehicles, vehicleLoading, 'vehicle')}
        {currentTab === 1 && renderTable(equipment, equipmentLoading, 'equipment')}
      </Box>

      {/* 新增/編輯對話框 */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          py: 3,
          mb: 0
        }}>
          {editingItem ? '編輯' : '新增'}{dialogType === 'vehicle' ? '車輛' : '機具'}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: '500px' }}>
            {/* 左側：輸入資料 */}
            <Box sx={{ 
              flex: 1, 
              p: 4, 
              borderRight: '1px solid #e0e0e0',
              background: '#fafafa'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 'bold' }}>
                    <DirectionsCar sx={{ mr: 1, verticalAlign: 'middle' }} />
                    基本資訊
                  </Typography>
                  <TextField
                    label="名稱"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                        borderWidth: 2,
                      },
                    }}
                  />
                </Box>
                
                {dialogType === 'vehicle' && (
                <Box>
                  <TextField
                    label="車牌號碼"
                    value={formData.license_plate}
                    onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                        borderWidth: 2,
                      },
                    }}
                  />
                </Box>
                )}
                
                {dialogType === 'vehicle' && (
                <Box>
                  <TextField
                    label="里程數"
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) || 0 }))}
                    fullWidth
                    inputProps={{ min: 0 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                        borderWidth: 2,
                      },
                    }}
                  />
                </Box>
                )}
                
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>狀態</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                      label="狀態"
                      sx={{
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                          borderWidth: 2,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                      }}
                    >
                      <MenuItem value="available">可用</MenuItem>
                      <MenuItem value="rented">租借中</MenuItem>
                      <MenuItem value="maintenance">維修中</MenuItem>
                      <MenuItem value="retired">已退役</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <TextField
                    label="備註說明"
                    multiline
                    rows={3}
                    value={formData.info}
                    onChange={(e) => setFormData(prev => ({ ...prev, info: e.target.value }))}
                    fullWidth
                    placeholder="請輸入相關備註資訊..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                        borderWidth: 2,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                        borderWidth: 2,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* 右側：照片管理 */}
            <Box sx={{ 
              flex: 1, 
              p: 4,
              background: '#ffffff',
              position: 'relative'
            }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#333', fontWeight: 'bold' }}>
                <Construction sx={{ mr: 1, verticalAlign: 'middle' }} />
                照片管理 (最多4張)
              </Typography>
              
              {/* Loading 覆蓋層 */}
              {isLoading && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  borderRadius: 2
                }}>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1, color: '#1976d2', fontWeight: 'bold' }}>
                    正在處理照片...
                  </Typography>
                  {uploadProgress.total > 0 && (
                    <Box sx={{ width: '80%', mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                        上傳進度: {uploadProgress.current}/{uploadProgress.total}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(uploadProgress.current / uploadProgress.total) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    請稍候，正在上傳照片並創建車輛記錄
                  </Typography>
                </Box>
              )}
              
              {/* 照片網格 */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 2,
                mb: 3
              }}>
                {[0, 1, 2, 3].map((index) => {
                  const photoNumber = index + 1;
                  const numberLabels = ['一', '二', '三', '四'];
                  const photoLabel = `第${numberLabels[index]}張`;
                  
                  return (
                    <Card 
                      key={index} 
                      sx={{ 
                        height: '140px',
                        border: photos[index] ? '2px solid #1976d2' : '2px dashed #e0e0e0',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: photos[index] ? '#f5f5f5' : '#fafafa',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => handlePhotoCardClick(index)}
                    >
                      {/* 照片編號標籤 */}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: index === 0 
                          ? '#4caf50' 
                          : 'rgba(25, 118, 210, 0.9)',
                        color: 'white',
                        py: 0.5,
                        px: 1,
                        zIndex: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '0.75rem'
                        }}>
                          {photoLabel}
                        </Typography>
                      </Box>

                      {(photos[index] || photoUrls[index] || photoLoading[index]) ? (
                        <Box sx={{ position: 'relative', width: '100%', height: '100%', pt: '28px' }}>
                          {photoLoading[index] && !photoUrls[index] ? (
                            <Box sx={{
                              width: '100%',
                              height: 'calc(100% - 28px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)'
                            }}>
                              <CircularProgress 
                                size={40}
                                sx={{
                                  color: index === 0 ? '#4caf50' : '#2196f3'
                                }}
                              />
                            </Box>
                          ) : (
                            <>
                              <img
                                src={photoUrls[index]}
                                alt={`照片 ${photoNumber}`}
                                style={{
                                  width: '100%',
                                  height: 'calc(100% - 28px)',
                                  objectFit: 'cover'
                                }}
                                onLoad={() => {
                                  // 照片載入完成後，關閉 loading
                                  setPhotoLoading(prev => {
                                    const newLoading = [...prev];
                                    newLoading[index] = false;
                                    return newLoading;
                                  });
                                }}
                                onError={(e) => {
                                  // 如果照片載入失敗，隱藏圖片並關閉 loading
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  setPhotoLoading(prev => {
                                    const newLoading = [...prev];
                                    newLoading[index] = false;
                                    return newLoading;
                                  });
                                }}
                              />
                              {/* 懸停時顯示的操作按鈕 */}
                              <Box
                                className="photo-actions"
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  display: 'flex',
                                  gap: 1,
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  zIndex: 2,
                                  '.MuiCard-root:hover &': {
                                    opacity: 1
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Tooltip title="更換照片">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                      }
                                    }}
                                    onClick={() => {
                                      const input = document.getElementById(`photo-upload-${index}`) as HTMLInputElement;
                                      if (input) input.click();
                                    }}
                                  >
                                    <CameraAlt fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="刪除照片">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                      }
                                    }}
                                    onClick={() => handlePhotoRemove(index)}
                                  >
                                    <DeleteOutline fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center', 
                          pt: '28px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1
                        }}>
                          <Add sx={{ fontSize: '2rem', color: '#9e9e9e', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            點擊上傳照片{photoNumber}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  );
                })}
              </Box>
              
              {/* 隱藏的照片上傳輸入框 */}
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  id={`photo-upload-${index}`}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handlePhotoSelect(e, index)}
                />
              ))}
              
              {/* 照片上傳說明 */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                  支援 JPG、PNG 格式，單張最大 5MB • 已上傳 {photos.filter(p => p).length}/4 張
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', gap: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2, 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            取消
          </Button>
          <Button 
            onClick={handleSaveItem} 
            variant="contained"
            sx={{ 
              borderRadius: 2, 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {editingItem ? '更新' : '新增'}{dialogType === 'vehicle' ? '車輛' : '機具'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 價格管理 Dialog */}
      <Dialog
        open={priceDialogOpen}
        onClose={handleClosePriceDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
            height: '82vh',
            maxHeight: '92vh',
            my: 2,
            display: 'flex',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
            color: 'white',
            fontSize: '1.2rem',
            fontWeight: 700,
            py: 1.25,
            px: 2.5,
            lineHeight: 1.35,
            mb: 0,
            borderBottom: '1px solid rgba(255,255,255,0.15)'
          }}
        >
          {priceKind === 'equipment' ? '機具價格管理' : '車輛價格管理'} {priceVehicle ? `- ${priceVehicle.name}` : ''}
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', fontSize: '1.06rem' }}>
          {priceLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
              <CircularProgress size={60} sx={{ color: '#2e7d32' }} />
              <Typography variant="body2" color="text.secondary">
                正在載入價格資料...
              </Typography>
            </Box>
          ) : (
            priceKind === 'equipment' ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, gap: 2, flex: 1, minHeight: 0 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.08rem' }}>
                    機具計價
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                    <TextField
                      label="整天價格(12H內)"
                      type="number"
                      value={equipmentPriceForm.day_rate}
                      onChange={(e) =>
                        setEquipmentPriceForm((prev) => ({
                          ...prev,
                          day_rate: Math.round(parseFloat(e.target.value) || 0),
                        }))
                      }
                      fullWidth
                      inputProps={{ step: '1', min: 0 }}
                    />
                    <TextField
                      label="超過後每小時價格"
                      type="number"
                      value={equipmentPriceForm.hourly_rate}
                      onChange={(e) =>
                        setEquipmentPriceForm((prev) => ({
                          ...prev,
                          hourly_rate: Math.round(parseFloat(e.target.value) || 0),
                        }))
                      }
                      fullWidth
                      inputProps={{ step: '1', min: 0 }}
                    />
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', minHeight: 0, overflow: 'auto' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.12rem' }}>
                    歷史價格紀錄
                  </Typography>
                  {(() => {
                    const grouped = equipmentPriceHistory.reduce((acc, row) => {
                      const dateKey = (row.created_at || '').slice(0, 10) || '無時間';
                      if (!acc[dateKey]) acc[dateKey] = [];
                      acc[dateKey].push(row);
                      return acc;
                    }, {} as Record<string, EquipmentPriceHistoryRow[]>);
                    const keys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
                    if (keys.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          沒有歷史價格紀錄
                        </Typography>
                      );
                    }
                    return keys.map((key) => (
                      <Accordion
                        key={key}
                        expanded={expandedHistoryKey === key}
                        onChange={(_, expanded) => setExpandedHistoryKey(expanded ? key : false)}
                        disableGutters
                        sx={{ mb: 1, borderRadius: 1.5, border: '1px solid #ececec', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography sx={{ fontWeight: 600, fontSize: '1.02rem' }}>{key}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          {grouped[key].map((row) => (
                            <Box key={row.id} sx={{ mb: 1.5, p: 1.2, bgcolor: '#f8f8f8', borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.9rem' }}>
                                {new Date(row.created_at).toLocaleString('zh-TW')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                整天價格(12H內): {row.day_rate ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                超過後每小時價格: {row.hourly_rate ?? '-'}
                              </Typography>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ));
                  })()}
                </Paper>
              </Box>
            ) : (
              <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.35fr 0.9fr' }, gap: 2, flex: 1, minHeight: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    '& .MuiInputLabel-root': { fontSize: '1.06rem' },
                    '& .MuiInputBase-input': { fontSize: '1.12rem' },
                  }}
                >
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.08rem' }}>基本計價</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                      <TextField
                        label="每小時計價"
                        type="number"
                        value={priceForm.hourly_rate}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, hourly_rate: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="半天門檻(小時)"
                        type="number"
                        value={priceForm.half_day_threshold_hours}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, half_day_threshold_hours: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="超過里程門檻(KM)"
                        type="number"
                        value={priceForm.extra_mileage_threshold_km}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, extra_mileage_threshold_km: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="每公里費用"
                        type="number"
                        value={priceForm.extra_mileage_rate_per_km}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setPriceForm(prev => ({ ...prev, extra_mileage_rate_per_km: Math.round(value * 10) / 10 }));
                        }}
                        fullWidth
                        inputProps={{ step: '0.1', min: 0 }}
                      />
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.08rem' }}>半天 / 整天（含司機）</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                      <TextField
                        label="半天不含司機"
                        type="number"
                        value={priceForm.half_day_without_driver_rate}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, half_day_without_driver_rate: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="半天含司機"
                        type="number"
                        value={priceForm.half_day_with_driver_rate}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, half_day_with_driver_rate: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="整天不含司機"
                        type="number"
                        value={priceForm.full_day_without_driver_rate}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, full_day_without_driver_rate: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                      <TextField
                        label="整天含司機"
                        type="number"
                        value={priceForm.full_day_with_driver_rate}
                        onChange={(e) => setPriceForm(prev => ({ ...prev, full_day_with_driver_rate: Math.round(parseFloat(e.target.value) || 0) }))}
                        fullWidth
                        inputProps={{ step: '1', min: 0 }}
                      />
                    </Box>
                  </Paper>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', minHeight: 0, overflow: 'auto' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.12rem' }}>
                    歷史價格紀錄
                  </Typography>
                  {(() => {
                    const grouped = priceHistory.reduce((acc, row) => {
                      const dateKey = (row.created_at || '').slice(0, 10) || '無時間';
                      if (!acc[dateKey]) acc[dateKey] = [];
                      acc[dateKey].push(row);
                      return acc;
                    }, {} as Record<string, VehiclePriceHistoryRow[]>);
                    const keys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
                    if (keys.length === 0) {
                      return <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>沒有歷史價格紀錄</Typography>;
                    }
                    return keys.map((key) => (
                      <Accordion
                        key={key}
                        expanded={expandedHistoryKey === key}
                        onChange={(_, expanded) => setExpandedHistoryKey(expanded ? key : false)}
                        disableGutters
                        sx={{ mb: 1, borderRadius: 1.5, border: '1px solid #ececec', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography sx={{ fontWeight: 600, fontSize: '1.02rem' }}>{key}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          {grouped[key].map((row) => (
                            <Box key={row.id} sx={{ mb: 1.5, p: 1.2, bgcolor: '#f8f8f8', borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.9rem' }}>
                                {new Date(row.created_at).toLocaleString('zh-TW')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                每小時計價: {row.hourly_rate ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                半天門檻(小時): {row.half_day_threshold_hours ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                超過里程門檻(KM): {row.extra_mileage_threshold_km ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                每公里費用: {row.extra_mileage_rate_per_km ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                半天(不含/含司機): {row.half_day_without_driver_rate ?? '-'} / {row.half_day_with_driver_rate ?? '-'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.98rem' }}>
                                整天(不含/含司機): {row.full_day_without_driver_rate ?? '-'} / {row.full_day_with_driver_rate ?? '-'}
                              </Typography>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ));
                  })()}
                </Paper>
              </Box>

              </>
            )
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.75, bgcolor: '#f5f5f5', borderTop: '1px solid #e9e9e9', gap: 1.5 }}>
          <Button
            onClick={handleClosePriceDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.18rem',
              fontWeight: 'bold'
            }}
          >
            取消
          </Button>
          <Button
            onClick={handleSavePriceDialog}
            variant="contained"
            disabled={priceSaving}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.18rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)',
              }
            }}
          >
            {priceSaving ? '儲存中...' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 頁面鎖定 Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        open={isLoading}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          backgroundColor: 'white',
          p: 4,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          <CircularProgress size={80} sx={{ mb: 3, color: '#1976d2' }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#333', fontWeight: 'bold' }}>
            正在處理中...
          </Typography>
          {uploadProgress.total > 0 && (
            <Box sx={{ width: '300px', mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1, textAlign: 'center', color: '#666' }}>
                照片上傳進度: {uploadProgress.current}/{uploadProgress.total}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(uploadProgress.current / uploadProgress.total) * 100}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
          )}
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            請稍候，正在上傳照片並創建車輛記錄
          </Typography>
        </Box>
      </Backdrop>

      {/* 訊息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 照片查看器 */}
      <Dialog
        open={photoViewer.open}
        onClose={() => setPhotoViewer({ open: false, src: '' })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', backgroundColor: 'transparent' }}>
          <IconButton
            onClick={() => setPhotoViewer({ open: false, src: '' })}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '70vh',
              p: 2,
            }}
          >
            <img
              src={photoViewer.src}
              alt="照片預覽"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EquipmentPage;